import { createContext, useContext, type ReactElement, type ReactNode } from 'react';

import type { Content } from '~/i18n/schema';
import type { Locale } from '~/i18n/config';

/**
 * The subset of Vike's pageContext that we surface to React components.
 *
 * Keeping it narrow avoids leaking Vike-only internals into the UI and lets
 * our tests fabricate a minimal context without mocking the whole framework.
 */
export interface AppPageContext {
  locale: Locale;
  content: Content;
  urlPathname: string;
}

const PageContext = createContext<AppPageContext | null>(null);

export function PageContextProvider(props: {
  pageContext: AppPageContext;
  children: ReactNode;
}): ReactElement {
  return <PageContext.Provider value={props.pageContext}>{props.children}</PageContext.Provider>;
}

export function usePageContext(): AppPageContext {
  const value = useContext(PageContext);
  if (value === null) {
    throw new Error('usePageContext must be used inside <PageContextProvider>');
  }
  return value;
}
